import React, { useState, useEffect, useCallback, useRef } from "react";
import { DialogLayout, Button, TextField } from "../../../../ui";
import { Dialog } from "../../../../ui/components/Dialog";
import { FeatherSearch, FeatherAlertCircle } from "@subframe/core";
import { CustomerInvoice } from "../../../../types";
import { getOutstandingInvoicesForCustomer } from "../../../../features/invoices/data/api/customersApi";
import { writeOffInvoice } from "../../../../features/invoices/data/api/invoicesApi";
import { useToast } from "../../../../shared/presentation/components";
import { ReviewRequestModal } from "../../../../shared/presentation/components/ReviewRequestModal";
import { usePermissions } from "../../../../shared/presentation/hooks/usePermissions";
import {
  MODULE_CODES,
  ACTION_CODES,
} from "../../../../shared/constants/permissions";

interface ViewOutstandingInvoicesModalProps {
  open: boolean;
  customerId: string;
  customerName: string;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful write-off so the parent can update outstanding/overdue counts locally */
  onWriteOffSuccess?: (params: {
    customerId: string;
    wasOverdue: boolean;
  }) => void;
}

const ViewOutstandingInvoicesModal: React.FC<
  ViewOutstandingInvoicesModalProps
> = ({ open, customerId, customerName, onOpenChange, onWriteOffSuccess }) => {
  const { showError, showSuccess } = useToast();
  const { getPermissionInfo } = usePermissions();

  // State for invoices
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [invoicesOffset, setInvoicesOffset] = useState(0);
  const [invoicesHasMore, setInvoicesHasMore] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState<string>("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // State for invoices (read-only display)
  const [invoices, setInvoices] = useState<CustomerInvoice[]>([]);

  // Write-off: confirmation and review modal
  const [confirmWriteOff, setConfirmWriteOff] = useState<{
    invoice: CustomerInvoice;
    action: "write_off" | "cancel_write_off";
  } | null>(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [pendingWriteOffAction, setPendingWriteOffAction] = useState<{
    invoice: CustomerInvoice;
    action: "write_off" | "cancel_write_off";
  } | null>(null);
  const [writeOffLoadingId, setWriteOffLoadingId] = useState<string | null>(
    null,
  );

  // Search timeout ref for debouncing
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevSearchTermRef = useRef<string>("");

  // Helper function to convert API response to CustomerInvoice (only local invoices are returned)
  const convertToCustomerInvoice = (item: any): CustomerInvoice => {
    return {
      id: item.id != null ? item.id : undefined,
      invoice_id: item.invoice_id,
      invoice_number: item.invoice_number,
      date: item.date,
      total: item.total,
      balance: item.balance,
      unprocessed_payment_amount: item.unprocessed_payment_amount,
      tax_total: item.tax_total,
      is_tds_applied: item.is_tds_applied,
      currency_code: item.currency_code,
      currency_symbol: item.currency_symbol,
      tds_tax_amount: item.tds_tax_amount,
      tds_section: item.tds_section ?? undefined,
      tds_tax_id: item.tds_tax_id ?? undefined,
      tds_tax_percentage: item.tds_tax_percentage,
      tds_tax_name: item.tds_tax_name ?? undefined,
      exchange_rate: item.exchange_rate,
      write_off_status: item.write_off_status ?? "none",
      is_under_review: item.is_under_review ?? 0,
    };
  };

  // Fetch outstanding invoices
  const fetchOutstandingInvoices = useCallback(
    async (reset: boolean = true) => {
      if (reset) {
        setInvoicesOffset(0);
        setIsLoadingMore(false);
        setLoadingInvoices(true);
      } else {
        setIsLoadingMore(true);
        setInvoicesLoading(true);
      }

      try {
        const result = await getOutstandingInvoicesForCustomer(
          customerId,
          20,
          reset ? 0 : invoicesOffset,
          invoiceSearchTerm || undefined,
        );

        // Convert to CustomerInvoice format
        const fetchedInvoices = result.data.map(convertToCustomerInvoice);

        // Access pagination properties (backend returns has_more, offset, limit but DTO type doesn't include them)
        const pagination = result.pagination as any;
        setInvoicesHasMore(pagination.has_more || false);
        setInvoicesOffset((pagination.offset || 0) + (pagination.limit || 20));

        if (reset) {
          setInvoices(fetchedInvoices);
        } else {
          setInvoices((prev) => [...prev, ...fetchedInvoices]);
        }
      } catch (error) {
        console.error("Failed to fetch outstanding invoices:", error);
        showError("Failed to fetch outstanding invoices. Please try again.");
        if (reset) {
          setInvoices([]);
        }
      } finally {
        if (reset) {
          setLoadingInvoices(false);
        } else {
          setIsLoadingMore(false);
          setInvoicesLoading(false);
        }
      }
    },
    [customerId, invoicesOffset, invoiceSearchTerm, showError],
  );

  const loadMoreInvoices = useCallback(() => {
    if (!invoicesHasMore || isLoadingMore || loadingInvoices) return;
    fetchOutstandingInvoices(false);
  }, [
    invoicesHasMore,
    isLoadingMore,
    loadingInvoices,
    fetchOutstandingInvoices,
  ]);

  // Fetch invoices when modal opens
  useEffect(() => {
    if (open) {
      fetchOutstandingInvoices(true);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search effect
  useEffect(() => {
    if (!open) return;

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const currentSearchTerm = invoiceSearchTerm.trim();
    const previousSearchTerm = prevSearchTermRef.current.trim();

    // If search term is empty and it was previously non-empty, refetch immediately
    if (!currentSearchTerm && previousSearchTerm) {
      prevSearchTermRef.current = currentSearchTerm;
      setInvoicesOffset(0);
      fetchOutstandingInvoices(true);
      return;
    }

    // If search term has value, debounce the search
    if (currentSearchTerm) {
      prevSearchTermRef.current = currentSearchTerm;
      searchTimeoutRef.current = setTimeout(() => {
        setInvoicesOffset(0);
        fetchOutstandingInvoices(true);
      }, 300);
    } else {
      prevSearchTermRef.current = currentSearchTerm;
    }

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceSearchTerm, open]);

  const handleClose = () => {
    setInvoices([]);
    setInvoiceSearchTerm("");
    setInvoicesOffset(0);
    setIsLoadingMore(false);
    onOpenChange(false);
  };

  // Format amount helper
  const formatAmount = (amount: number): string => {
    return amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleWriteOffClick = (
    invoice: CustomerInvoice,
    action: "write_off" | "cancel_write_off",
  ) => {
    const actionCode =
      action === "write_off"
        ? ACTION_CODES.WRITE_OFF
        : ACTION_CODES.CANCEL_WRITE_OFF;
    const info = getPermissionInfo(MODULE_CODES.INVOICE, actionCode);
    if (!info.granted) {
      showError("You do not have permission to perform this action.");
      return;
    }
    setConfirmWriteOff({ invoice, action });
  };

  const handleConfirmWriteOffYes = useCallback(() => {
    if (!confirmWriteOff) return;
    const { invoice, action } = confirmWriteOff;
    const localId = invoice.id != null ? Number(invoice.id) : null;
    const zohoInvoiceId = invoice.invoice_id ?? undefined;
    const pathId = localId ?? zohoInvoiceId;
    if (pathId == null || pathId === "") {
      showError("Invoice not valid. Please contact support.");
      setConfirmWriteOff(null);
      return;
    }
    const actionCode =
      action === "write_off"
        ? ACTION_CODES.WRITE_OFF
        : ACTION_CODES.CANCEL_WRITE_OFF;
    const permInfo = getPermissionInfo(MODULE_CODES.INVOICE, actionCode);

    const needsReview = permInfo.requiresReview;

    if (needsReview) {
      setPendingWriteOffAction({ invoice, action });
      setReviewModalOpen(true);
      setConfirmWriteOff(null);
      return;
    }

    const loadingKey =
      invoice.id != null ? String(invoice.id) : invoice.invoice_id;
    setWriteOffLoadingId(loadingKey);
    const apiCall = writeOffInvoice(pathId);

    apiCall
      .then(() => {
        if (action === "write_off") {
          showSuccess("Invoice has been written off.");
          setInvoices((prev) =>
            prev.filter((inv) => {
              if (localId != null)
                return inv.id == null || Number(inv.id) !== localId;
              return inv.invoice_id !== invoice.invoice_id;
            }),
          );
          if (onWriteOffSuccess) {
            onWriteOffSuccess({
              customerId,
              wasOverdue: (invoice.status ?? "").toLowerCase() === "overdue",
            });
          }
        } else {
          showSuccess("Write off cancelled successfully.");
          setInvoices((prev) =>
            prev.map((inv) => {
              if (
                localId != null &&
                inv.id != null &&
                Number(inv.id) === localId
              )
                return { ...inv, write_off_status: "none" as const };
              if (localId == null && inv.invoice_id === invoice.invoice_id)
                return { ...inv, write_off_status: "none" as const };
              return inv;
            }),
          );
        }
        setConfirmWriteOff(null);
      })
      .catch((err) => {
        showError(err?.message || "Action failed. Please try again.");
      })
      .finally(() => {
        setWriteOffLoadingId(null);
        setConfirmWriteOff(null);
      });
  }, [
    confirmWriteOff,
    customerId,
    getPermissionInfo,
    onWriteOffSuccess,
    showError,
    showSuccess,
  ]);

  const handleReviewSuccess = useCallback(() => {
    const invoice = pendingWriteOffAction?.invoice;
    if (invoice) {
      const matchByLocalId = invoice.id != null;
      setInvoices((prev) =>
        prev.map((inv) => {
          if (
            matchByLocalId &&
            inv.id != null &&
            Number(inv.id) === Number(invoice.id)
          )
            return { ...inv, is_under_review: 1 };
          if (!matchByLocalId && inv.invoice_id === invoice.invoice_id)
            return { ...inv, is_under_review: 1 };
          return inv;
        }),
      );
    }
    setPendingWriteOffAction(null);
    setReviewModalOpen(false);
  }, [pendingWriteOffAction]);

  return (
    <DialogLayout open={open} onOpenChange={handleClose}>
      <div className="flex w-256 flex-col items-start gap-6 bg-default-background px-8 py-6">
        <span className="text-heading-2 font-heading-2 text-default-font">
          Outstanding Invoices - {customerName}
        </span>

        <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-neutral-50 px-8 py-6 shadow-sm">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-3 font-heading-3 text-default-font">
              {customerName}
            </span>
          </div>
        </div>

        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Outstanding Invoices
            </span>
            <div className="flex items-center gap-2">
              <TextField
                className="h-8 w-48"
                label=""
                helpText=""
                icon={<FeatherSearch />}
              >
                <TextField.Input
                  placeholder="Search invoices"
                  value={invoiceSearchTerm}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setInvoiceSearchTerm(event.target.value);
                  }}
                />
              </TextField>
            </div>
          </div>
          <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
            <div className="flex w-full flex-col items-start gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Available Invoices
                {loadingInvoices && (
                  <span className="text-body font-body text-neutral-500 ml-2">
                    (Loading...)
                  </span>
                )}
              </span>
            </div>
            <div className="flex w-full flex-col items-center gap-3 px-4">
              {invoices.length > 0 ? (
                invoices.map((invoice) => {
                  const paid = invoice.total - invoice.balance;
                  const isUnderReview =
                    invoice.is_under_review === 1 ||
                    invoice.is_under_review === "1";
                  return (
                    <div
                      key={invoice.invoice_id}
                      className="flex w-full items-center justify-between rounded-md border border-solid border-neutral-border bg-default-background px-4 py-3"
                    >
                      <div className="flex flex-col items-start flex-shrink-0">
                        <span className="text-body-bold font-body-bold text-default-font">
                          {invoice.invoice_number}
                        </span>
                        <span className="text-body font-body text-neutral-500">
                          {new Date(invoice.date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-8 flex-shrink-0">
                        <div className="flex flex-col items-center w-32">
                          <span className="text-caption font-caption text-neutral-400">
                            Full
                          </span>
                          <span className="text-body font-body text-default-font">
                            {invoice.currency_symbol}
                            {formatAmount(invoice.total)}
                          </span>
                        </div>
                        <div className="flex flex-col items-center w-32">
                          <span className="text-caption font-caption text-neutral-400">
                            Paid
                          </span>
                          <span className="text-body font-body text-default-font">
                            {invoice.currency_symbol}
                            {formatAmount(paid)}
                          </span>
                        </div>
                        <div className="flex flex-col items-center w-32">
                          <span className="text-caption font-caption text-neutral-400">
                            Remaining
                          </span>
                          <span className="text-body font-body text-default-font">
                            {invoice.currency_symbol}
                            {formatAmount(invoice.balance)}
                          </span>
                        </div>
                        {isUnderReview && (
                          <span className="rounded-md border border-solid border-warning-200 bg-warning-50 px-2 py-0.5 text-caption font-caption text-warning-700">
                            Under review
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : !loadingInvoices ? (
                <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-6">
                  <span className="text-body font-body text-neutral-500">
                    No outstanding invoices found for {customerName}
                  </span>
                </div>
              ) : null}
              {invoicesHasMore && (
                <div className="flex w-full items-center justify-center pt-2">
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    onClick={loadMoreInvoices}
                    disabled={isLoadingMore || loadingInvoices}
                    loading={isLoadingMore}
                  >
                    {isLoadingMore ? "Loading..." : "Load More Invoices"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex w-full items-center justify-end border-t border-solid border-neutral-border pt-6">
          <Button variant="neutral-tertiary" onClick={handleClose}>
            Close
          </Button>
        </div>
      </div>

      {/* Confirmation dialog for write off / cancel write off */}
      {confirmWriteOff && (
        <Dialog
          open={!!confirmWriteOff}
          onOpenChange={(open) => !open && setConfirmWriteOff(null)}
        >
          <Dialog.Content className="min-w-[420px] max-w-[520px]">
            <div className="flex w-full flex-col items-start gap-6 px-6 py-6">
              <div className="flex w-full items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-neutral-100">
                  <FeatherAlertCircle className="h-5 w-5 text-neutral-600" />
                </div>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {confirmWriteOff.action === "write_off"
                    ? "Write off invoice"
                    : "Cancel write off"}
                </span>
              </div>
              <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-200 bg-neutral-50 px-4 py-4">
                <span className="text-body font-body text-neutral-700 leading-relaxed">
                  {confirmWriteOff.action === "write_off"
                    ? "Are you sure you want to write off this invoice?"
                    : "Are you sure you want to cancel write off for this invoice?"}
                </span>
              </div>
              {/* <div className="flex w-full items-center justify-end gap-3 pt-1">
                <Button
                  variant="neutral-secondary"
                  onClick={() => setConfirmWriteOff(null)}
                  disabled={!!writeOffLoadingId}
                >
                  Cancel
                </Button>
                <Button
                  variant="brand-primary"
                  onClick={handleConfirmWriteOffYes}
                  disabled={!!writeOffLoadingId}
                  loading={!!writeOffLoadingId}
                >
                  {confirmWriteOff.action === "write_off"
                    ? "Write off"
                    : "Cancel write off"}
                </Button>
              </div> */}
            </div>
          </Dialog.Content>
        </Dialog>
      )}

      {/* Review request modal for write off when review is required */}
      {pendingWriteOffAction && (
        <ReviewRequestModal
          open={reviewModalOpen}
          onOpenChange={setReviewModalOpen}
          moduleCode={MODULE_CODES.INVOICE}
          actionCode={
            pendingWriteOffAction.action === "write_off"
              ? ACTION_CODES.WRITE_OFF
              : ACTION_CODES.CANCEL_WRITE_OFF
          }
          entityType="invoice"
          entityId={
            pendingWriteOffAction.invoice.id != null
              ? Number(pendingWriteOffAction.invoice.id)
              : -1
          }
          documentNumber={pendingWriteOffAction.invoice.invoice_number}
          changeDetails={{
            action_types: [pendingWriteOffAction.action],
            raw_json: {
              invoice_id: pendingWriteOffAction.invoice.invoice_id,
              invoice_number: pendingWriteOffAction.invoice.invoice_number,
              total: pendingWriteOffAction.invoice.total,
              balance: pendingWriteOffAction.invoice.balance,
              paid:
                pendingWriteOffAction.invoice.total -
                pendingWriteOffAction.invoice.balance,
            },
          }}
          skipNavigation={true}
          onSuccess={handleReviewSuccess}
        />
      )}
    </DialogLayout>
  );
};

export default ViewOutstandingInvoicesModal;
